import BaseInteraction, { CleanupMethod } from './BaseInteraction';
import ContentItem from '../api/content/ContentItem';
import bindHLSjsToWebRTC, {
  HLSjsInteractionOptions
} from '../webrtc/bindHLSjsToWebRTC';
import attachHlsEvents from './utils/attachHlsEvents';
import logger from '../log';
import { HLS } from './interfaces';
import { SourceSetEventOptions } from '../events';
import { SourceType } from '../common/constants';
import { checkType, Types } from '../util/checkTypes';
import { dispatchError } from '../events/dispatch';

const log = logger.getLogger(__filename);

/**
 * Does everything for HLSjs except the setSource and clearSource functions,
 * and doesn't check to make sure the hls object passed in is valid, as the
 * Flowplayer implementation can have the hls object instantiated later.
 */
export default abstract class BaseHlsJsInteraction<
  T extends HTMLVideoElement
> extends BaseInteraction<T> {
  hls?: HLS;
  option?: HLSjsInteractionOptions;
  hlsCleanupMethods: CleanupMethod[];
  constructor(player: T, hls?: HLS, option?: HLSjsInteractionOptions) {
    super(player);
    this.option = option;
    this.hlsCleanupMethods = [];

    if (!player) {
      const msg = 'Unable to bind properly, no video element supplied';
      log.error(msg);
      dispatchError(msg);
    }
    this.hls = hls;
    if (hls) {
      this.bindHls();
    }
  }

  loader(items: ContentItem[]): ContentItem[] {
    const [firstSrc] = items;
    const { url } = firstSrc;
    this.setSource(url);
    return [firstSrc];
  }

  abstract setSource(url: string): void;

  mediaElement(): HTMLMediaElement {
    return this.player as HTMLMediaElement;
  }

  getAllowedSourceTypes(): SourceType[] {
    return [SourceType.HLS];
  }

  resetEventsAndInterceptors(opts: SourceSetEventOptions<T>): void {
    this.resetHls(opts.options?.hls);
    const ele = opts.options?.ele ?? opts.options?.player;
    this.resetMediaElement(ele as T);
  }

  protected resetHls(hls?: HLS | null): void {
    if (hls === null || (hls && hls !== this.hls)) {
      this.hlsCleanupMethods.forEach(method => method());
      this.hlsCleanupMethods = [];
      this.hls = hls as HLS;
      if (hls) {
        checkType(hls, Types.HLS).emitErrorIfNotCorrectType();
        this.bindHls();
      }
    }
  }

  /**
   * Attach to all of the HLS events so we can track bitrates, etc. and
   * bind the interceptors to the HLS object.
   */
  protected bindHls(): void {
    if (!this.hls) {
      return;
    }
    this.hlsCleanupMethods.push(attachHlsEvents(this.hls));
    bindHLSjsToWebRTC(this.hls, this.binder, this.option);
  }

  cleanup(): void {
    this.resetHls(null);
    super.cleanup();
  }
}
